import Swiper, { Grid, Navigation } from 'swiper';

const heightControllers = new WeakMap();
const titleControllers = new WeakMap();
const DESKTOP_BREAKPOINT = 1024;
const desktopSlideOrder = [0, 4, 7, 1, 5, 8, 2, 6, 9, 3];

const getBaseGridRows = (slidesCount) => {
  if (slidesCount <= 3) {
    return 1;
  }

  if (slidesCount <= 6) {
    return 2;
  }

  return 3;
};

const getDesktopGridRows = (slidesCount, baseRows) => (slidesCount >= 10 ? 4 : baseRows);

const syncGridFillClass = (swiper) => {
  const gridColumnClass = `${swiper.params.containerModifierClass}grid-column`;

  swiper.el.classList.toggle(gridColumnClass, swiper.params.grid.fill === 'column');
};

const syncDesktopSlideOrder = (swiperEl) => {
  const wrapperEl = swiperEl.querySelector('.swiper-wrapper');
  const slides = Array.from(wrapperEl?.children || []).filter((slide) => slide.classList.contains('swiper-slide'));

  if (slides.length !== desktopSlideOrder.length) {
    return false;
  }

  slides.forEach((slide, index) => {
    if (!slide.dataset.frameworkSlideIndex) {
      slide.dataset.frameworkSlideIndex = String(index);
    }
  });

  const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
  const orderedSlides = [...slides].sort((firstSlide, secondSlide) => {
    const firstIndex = Number(firstSlide.dataset.frameworkSlideIndex);
    const secondIndex = Number(secondSlide.dataset.frameworkSlideIndex);
    const firstOrder = isDesktop ? desktopSlideOrder.indexOf(firstIndex) : firstIndex;
    const secondOrder = isDesktop ? desktopSlideOrder.indexOf(secondIndex) : secondIndex;

    return firstOrder - secondOrder;
  });

  if (slides.every((slide, index) => slide === orderedSlides[index])) {
    return false;
  }

  orderedSlides.forEach((slide) => {
    wrapperEl.append(slide);
  });

  return true;
};

const getSwiperRowGroups = (swiperEl, swiper) => {
  const slides = Array.from(swiperEl.querySelectorAll('.swiper-slide'));
  const rows = Math.max(1, Number(swiper.params.grid?.rows) || 1);

  if (swiper.params.grid?.fill === 'row') {
    const columns = Math.max(1, Math.ceil(slides.length / rows));

    return Array.from({ length: Math.ceil(slides.length / columns) }, (_, index) => (
      slides.slice(index * columns, (index + 1) * columns)
    ));
  }

  const groups = Array.from({ length: rows }, () => []);
  slides.forEach((slide, index) => groups[index % rows].push(slide));

  return groups.filter((group) => group.length);
};

const createLineTitlesController = (swiperEl, swiper, onTitlesUpdated) => {
  if (typeof window.lineTitlesFunc !== 'function') {
    return null;
  }

  const titles = Array.from(swiperEl.querySelectorAll('.framework-sec__item-title'));

  if (!titles.length) {
    return null;
  }

  const getGroups = () => {
    return getSwiperRowGroups(swiperEl, swiper).map((group) => (
      group.map((slide) => slide.querySelector('.framework-sec__item-title'))
    ));
  };

  const controller = window.lineTitlesFunc(titles, { getGroups });

  return {
    refresh: () => {
      controller.refresh();
      requestAnimationFrame(() => requestAnimationFrame(onTitlesUpdated));
    },
    destroy: () => controller.destroy(),
  };
};

const createHeightController = (swiperEl, swiper, syncSlidesOrder) => {
  let resizeTimeout = null;
  let resizeFrame = null;

  const setStableHeight = () => {
    if (swiper.destroyed) {
      return;
    }

    const wrapperEl = swiperEl.querySelector('.swiper-wrapper');
    const slides = Array.from(swiperEl.querySelectorAll('.swiper-slide'));
    const items = Array.from(swiperEl.querySelectorAll('.framework-sec__item'));

    if (!wrapperEl || !slides.length || !items.length) {
      return;
    }

    swiperEl.style.height = 'auto';
    wrapperEl.style.height = 'auto';
    slides.forEach((slide) => {
      slide.style.height = 'auto';
    });

    items.forEach((item) => {
      item.style.minHeight = '';
    });

    const rowGroups = getSwiperRowGroups(swiperEl, swiper);
    const rowHeights = rowGroups.map((group) => {
      const rowItems = group
        .map((slide) => slide.querySelector('.framework-sec__item'))
        .filter(Boolean);

      if (!rowItems.length) {
        return 0;
      }

      const rowHeight = Math.ceil(Math.max(...rowItems.map((item) => item.offsetHeight)));

      rowItems.forEach((item) => {
        item.style.minHeight = `${rowHeight}px`;
      });

      group.forEach((slide) => {
        slide.style.height = `${rowHeight}px`;
      });

      return rowHeight;
    });

    const maxItemHeight = Math.max(...rowHeights);
    const gap = Number.parseFloat(swiper.params.spaceBetween) || 0;
    const slideHeight = Math.ceil(maxItemHeight);
    const height = rowHeights.reduce((total, rowHeight) => total + rowHeight, 0)
      + gap * Math.max(0, rowHeights.length - 1);

    swiperEl.style.removeProperty('height');
    wrapperEl.style.removeProperty('height');
    swiperEl.style.setProperty('--framework-swiper-height', `${height}px`);
    swiperEl.style.setProperty('--framework-swiper-gap', `${gap}px`);
    swiperEl.style.setProperty('--framework-swiper-slide-height', `${slideHeight}px`);
    swiper.update();
  };

  const requestStableHeight = () => {
    if (resizeFrame) {
      cancelAnimationFrame(resizeFrame);
    }

    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = null;
      setStableHeight();
    });
  };

  const onResize = () => {
    if (resizeTimeout) {
      return;
    }

    resizeTimeout = setTimeout(() => {
      resizeTimeout = null;
      requestStableHeight();
    }, 150);
  };

  requestStableHeight();
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('orientationchange', onResize, { passive: true });
  swiper.on('breakpoint', () => {
    syncGridFillClass(swiper);

    if (syncSlidesOrder()) {
      swiper.update();
    }

    requestStableHeight();
  });

  if (document.fonts?.ready) {
    document.fonts.ready.then(requestStableHeight).catch(requestStableHeight);
  } else {
    window.addEventListener('load', requestStableHeight, { once: true });
  }

  return { requestStableHeight };
};

const initFrameworkSwiper = (scope = document) => {
  const wrappers = scope.querySelectorAll('.framework-swiper-wr');

  wrappers.forEach((wrapper) => {
    const swiperEl = wrapper.querySelector('.swiper');

    if (!swiperEl) {
      return;
    }

    const slidesCount = swiperEl.querySelectorAll('.swiper-slide').length;

    if (!slidesCount) {
      return;
    }

    const baseRows = getBaseGridRows(slidesCount);
    const desktopRows = getDesktopGridRows(slidesCount, baseRows);
    const syncSlidesOrder = () => syncDesktopSlideOrder(swiperEl);

    const slidesOrderChanged = syncSlidesOrder();

    if (swiperEl.swiper) {
      if (slidesOrderChanged) {
        swiperEl.swiper.update();
      }

      titleControllers.get(wrapper)?.refresh();
      heightControllers.get(wrapper)?.requestStableHeight();
      return;
    }

    const nextEl = wrapper.querySelector('.swiper-button-next');
    const prevEl = wrapper.querySelector('.swiper-button-prev');
    const swiper = new Swiper(swiperEl, {
      modules: [Grid, Navigation],
      observer: true,
      observeParents: true,
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 12,
      speed: 500,
      threshold: 10,
      watchOverflow: true,
      roundLengths: true,
      grid: {
        rows: baseRows,
        fill: 'column',
      },
      navigation: nextEl && prevEl ? {
        nextEl,
        prevEl,
      } : undefined,
      breakpoints: {
        552: {
          slidesPerView: 2,
          spaceBetween: 12,
          grid: {
            rows: baseRows,
            fill: 'column',
          },
        },
        641: {
          slidesPerView: 3,
          spaceBetween: 16,
          grid: {
            rows: baseRows,
            fill: 'column',
          },
        },
        768: {
          slidesPerView: 3,
          spaceBetween: 18,
          grid: {
            rows: baseRows,
            fill: 'column',
          },
        },
        820: {
          slidesPerView: 3,
          spaceBetween: 20,
          grid: {
            rows: baseRows,
            fill: 'column',
          },
        },
        951: {
          slidesPerView: 3,
          spaceBetween: 22,
          grid: {
            rows: baseRows,
            fill: 'column',
          },
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 22,
          grid: {
            rows: desktopRows,
            fill: 'row',
          },
        },
        1101: {
          slidesPerView: 3,
          spaceBetween: 24,
          grid: {
            rows: desktopRows,
            fill: 'row',
          },
        },
      },
    });

    swiper.update();

    const heightController = createHeightController(swiperEl, swiper, syncSlidesOrder);
    const titleController = createLineTitlesController(
      swiperEl,
      swiper,
      heightController.requestStableHeight,
    );

    if (titleController) {
      titleControllers.set(wrapper, titleController);
      swiper.on('breakpoint', titleController.refresh);
      titleController.refresh();
    }

    heightControllers.set(wrapper, heightController);
  });
};

initFrameworkSwiper();
window.initFrameworkSwiper = initFrameworkSwiper;
